import React, { useEffect, useState, useRef } from "react";
import { marked } from "marked";
import { connect } from "react-redux";
import { toast } from "react-toastify";

import EventTypeForm from "../EventDetailsForm/EventTypeForm";
import { eventBookingActions } from "../../../../redux/actions";
import EventOnsiteForm from "../EventDetailsForm/EventOnsiteForm";
import EventSelection from "../../ShutterBSelection/EventSelection";
import EventTypeAIResponse from "../EventDetailsForm/EventTypeAIResponse";
import useResponsiveView from "../../../../shared/hooks/useResponsiveView";
import BackButton from "../../../../shared/components/IconButton/BackButton";
import {
  messages,
  EMPTY_STRING,
  eventDetailsFields,
  eventBookingStatus,
  eventBookingConstants,
} from "../../../../constants";
import {
  customToast,
  filterByStatus,
  removeDraftEvents,
  isEventDetailsValid,
  updateEventDetailAIResponse,
} from "../../../../shared";

const { TABS_ID } = eventBookingConstants;

export const EVENT_PHASES = {
  AI_RESPONSE: "AI_RESPONSE",
  CONTACT_INFO: "CONTACT_INFO",
};

const EventDetails = (props) => {
  const { currentEventType, onNext } = props;

  // Check if the viewport at specific width
  const { isGreaterThan } = useResponsiveView();

  const [currentPhase, setCurrentPhase] = useState(EVENT_PHASES.AI_RESPONSE);
  const [currentContent, setCurrentContent] = useState(EMPTY_STRING);
  const [shouldScroll, setShouldScroll] = useState(false);
  const aiResponseRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line
  }, [currentPhase]);

  useEffect(() => {
    if (props.activeTab === TABS_ID.EVENT_DETAIL) {
      window.scrollTo(0, 0);

      // Remove draft and Canceled events from the formState
      const updatedEvents = removeDraftEvents(
        filterByStatus(props.formState ?? [], [eventBookingStatus.CANCEL]),
      );
      if (updatedEvents.length > 0) {
        const firstEvent = updatedEvents[0];
        props.setEventName(firstEvent.eventName);
        props.setSelectedEventId(firstEvent.id);
      } else {
        props.setSelectedEventId(null);
        props.setEventName(EMPTY_STRING);
      }
      setCurrentPhase(EVENT_PHASES.AI_RESPONSE);
    }
    // eslint-disable-next-line
  }, [props.activeTab]);

  // Update currentEventType when selectedEventId or formstate change
  useEffect(() => {
    const currentEvent = Array.isArray(props.formState)
      ? props.formState.find((event) => event.id === props.selectedEventId)
      : null;
    props.setCurrentEventType(currentEvent || null);
    setCurrentContent(
      currentEvent?.eventType?.description
        ? marked.parse(currentEvent.eventType.description)
        : EMPTY_STRING,
    );
    // eslint-disable-next-line
  }, [props.selectedEventId, props.formState]);

  const getValidEvents = () =>
    removeDraftEvents(
      filterByStatus(props.formState ?? [], [eventBookingStatus.CANCEL]),
    );

  const handleAINext = () => {
    if (!isEventDetailsValid(currentEventType.eventType)) {
      customToast(messages.EVENT_SHOT_LIST_IS_EMPTY, toast.TYPE.ERROR);
      return;
    }

    const events = getValidEvents();

    const currentIndex = events.findIndex(
      (e) => e.id === props.selectedEventId,
    );

    const isLastEvent = currentIndex === events.length - 1;

    if (isLastEvent) {
      // All AI responses completed
      setCurrentPhase(EVENT_PHASES.CONTACT_INFO);

      const firstEvent = events[0];

      if (firstEvent) {
        props.setSelectedEventId(firstEvent.id);
        props.setEventName(firstEvent.eventName);
      }
    } else {
      const nextEvent = events[currentIndex + 1];

      props.setSelectedEventId(nextEvent.id);
      props.setEventName(nextEvent.eventName);
    }
  };

  const handleContactPrevious = () => {
    const events = getValidEvents();

    const currentIndex = events.findIndex(
      (e) => e.id === props.selectedEventId,
    );

    if (currentIndex === 0) {
      setCurrentPhase(EVENT_PHASES.AI_RESPONSE);

      const lastEvent = events[events.length - 1];

      if (lastEvent) {
        props.setSelectedEventId(lastEvent.id);
        props.setEventName(lastEvent.eventName);
      }

      return;
    }

    const previousEvent = events[currentIndex - 1];

    props.setSelectedEventId(previousEvent.id);
    props.setEventName(previousEvent.eventName);
  };

  const handleEventSlider = (event) => {
    if (
      props.currentEventType?.id === props.selectedEventId &&
      props.currentEventType?.eventType?.description !== currentContent &&
      currentContent !== EMPTY_STRING &&
      currentPhase === EVENT_PHASES.AI_RESPONSE
    ) {
      const updatedFormState = updateEventDetailAIResponse({
        formState: props.formState,
        eventId: props.selectedEventId,
        eventType: eventDetailsFields.EVENT_TYPE,
        description: eventDetailsFields.DESCRIPTION,
        value: currentContent,
      });
      props.setFormState(updatedFormState);
    }

    props.setSelectedEventId(event.id);
    props.setEventName(event.eventName);
    setCurrentPhase(EVENT_PHASES.AI_RESPONSE);
  };

  const handleChange = (eventId, name, value) => {
    if (!eventId) return;

    const updatedFormState = props.formState.map((event) => {
      if (event.id !== eventId) return event;

      if (name === eventDetailsFields.EVENT_QUESTION_ANSWERS) {
        const updatedAnswers = [
          ...(event.eventQuestionAnswers || []).filter(
            (q) => q.questionTypeId !== value.questionTypeId,
          ),
          value,
        ];

        return {
          ...event,
          eventQuestionAnswers: updatedAnswers,
        };
      }

      if (name === eventDetailsFields.EVENT_TYPE) {
        // Merge into the existing eventType object without removing other fields
        return {
          ...event,
          [eventDetailsFields.EVENT_TYPE]: {
            ...(event.eventType || {}),
            ...value,
          },
        };
      }

      return {
        ...event,
        [name]: value,
      };
    });
    props.setFormState(updatedFormState);
  };

  //Scroll to EventTypeAIResponse
  useEffect(() => {
    if (shouldScroll && aiResponseRef.current) {
      aiResponseRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setShouldScroll(false);
    }
  }, [shouldScroll, aiResponseRef, props.currentEventType]);

  const scrollToAIResponse = () => {
    // Only scroll on tablet or mobile views
    if (isGreaterThan(992)) return;

    // If already rendered, scroll immediately; otherwise set flag for effect to pick up after render
    if (aiResponseRef.current) {
      aiResponseRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      setShouldScroll(true);
    }
  };

  const handlePrevious = () => {
    props.setEventName(EMPTY_STRING);
    props.onPrevious();
  };

  const handlePhaseChange = (phase) => {
    if (
      phase === EVENT_PHASES.CONTACT_INFO &&
      !isEventDetailsValid(currentEventType?.eventType)
    ) {
      customToast(messages.EVENT_SHOT_LIST_IS_EMPTY, toast.TYPE.ERROR);
      return;
    }
    setCurrentPhase(phase);
  };

  const canProceedToContact = () => {
    return isEventDetailsValid(currentEventType?.eventType);
  };

  return (
    <div className="event-details-container">
      <div className="event-detail-type-container">
        <button
          type="button"
          className={`button-common ${
            currentPhase === EVENT_PHASES.AI_RESPONSE
              ? "button-fill"
              : "button-outline"
          }`}
          onClick={() => handlePhaseChange(EVENT_PHASES.AI_RESPONSE)}
        >
          ai shot list
        </button>

        <button
          type="button"
          className={`button-common  ${
            currentPhase === EVENT_PHASES.CONTACT_INFO
              ? "button-fill"
              : "button-outline"
          }`}
          onClick={() => handlePhaseChange(EVENT_PHASES.CONTACT_INFO)}
        >
          contact info
        </button>
      </div>

      <EventSelection
        formState={removeDraftEvents(
          filterByStatus(props.formState ?? [], [eventBookingStatus.CANCEL]),
        )}
        handleEventClick={handleEventSlider}
        isDisabled
      />
      <div className="row">
        <div className="col-12">
          <hr className="mt-0" />
        </div>
      </div>

      <div className="row event-type-container">
        {currentPhase === EVENT_PHASES.AI_RESPONSE ? (
          <>
            <div className="col-lg-6 pr-md-0">
              <EventTypeForm
                onChange={handleChange}
                onGenerateShotlist={scrollToAIResponse}
              />
            </div>
            {currentEventType?.eventType?.description?.length > 0 && (
              <div className="col-lg-6 pl-md-0">
                <EventTypeAIResponse
                  ref={aiResponseRef}
                  onNext={handleAINext}
                  currentContent={currentContent}
                  setCurrentContent={setCurrentContent}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div className="col-12">
              <EventOnsiteForm
                formState={removeDraftEvents(
                  filterByStatus(props.formState ?? [], [
                    eventBookingStatus.CANCEL,
                  ]),
                )}
                setCurrentPhase={setCurrentPhase}
                onChange={handleChange}
                onPrevious={handleContactPrevious}
                onNext={onNext}
              />
            </div>
          </>
        )}
      </div>

      <BackButton
        className="event-back-button"
        onClick={handlePrevious}
        text={`${props.previousActiveTab}`}
      />
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  formState: state.eventBooking.formState,
  selectedEventId: state.eventBooking.selectedEventId,
  currentEventType: state.eventBooking.currentEventType,
  previousActiveTab: state.eventBooking.previousActiveTab,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setEventName: (data) => dispatch(eventBookingActions.setEventName(data)),
  setSelectedEventId: (data) =>
    dispatch(eventBookingActions.setSelectedEventId(data)),
  setCurrentEventType: (data) =>
    dispatch(eventBookingActions.setCurrentEventType(data)),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(EventDetails);
